"use client";

import { useState, useEffect, useMemo } from "react";

import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { HeroSection } from "@/components/hero-section";
import { useInView } from "@/hooks/useInView";
import { useLanguage } from "@/context/LanguageContext";
import { contentInfoApi, webInfoApi } from "@/lib/api";

import dynamic from "next/dynamic";

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

interface Section {
  number: number;
  title: string;
  content: string[]; // lines
  icon: string;
  anchor: string;
}

const ICONS: Record<number, string> = {
  1: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" /></svg>`,
  2: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>`,
  3: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`,
  4: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>`,
  5: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>`,
  6: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" /></svg>`,
  7: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>`,
  8: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" /></svg>`,
  9: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>`,
  10: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>`,
  11: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>`,
  12: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V7.875a4.5 4.5 0 10-9 0V10.5m-.75 0h10.5A2.25 2.25 0 0119.5 12.75v6A2.25 2.25 0 0117.25 21H6.75A2.25 2.25 0 014.5 18.75v-6A2.25 2.25 0 016.75 10.5z" /></svg>`,

};

const slugify = (text: string) => text.toLowerCase().replace(/[^\w]+/g, "-");

function parsePolicy(content: string) {
  const lines = (content || "").replace(/\r\n/g, "\n").split("\n");

  let title = "Privacy Policy";
  let effectiveDate = "";
  const sections: Section[] = [];

  let current: Section | null = null;

  for (const line of lines) {
    // Title: "# Privacy Policy"
    if (line.startsWith("# ")) {
      title = line.replace("# ", "").trim();
      continue;
    }

    // Effective Date: "**Effective Date: ...**" (optional)
    if (line.toLowerCase().includes("effective date")) {
      effectiveDate = line.replace(/\*\*/g, "").trim();
      continue;
    }

    // Section header styles we support:
    // "## 1. Information We Collect"
    // "1. Information We Collect"
    const h2Match = line.match(/^##\s+(\d+)\.\s+(.+)/);
    const plainMatch = line.match(/^(\d+)\.\s*(.+)$/);

    const match = h2Match || plainMatch;

    if (match) {
      if (current) sections.push(current);

      const number = Number(match[1]);
      const sectionTitle = match[2].trim();

      current = {
        number,
        title: sectionTitle,
        content: [],
        icon: ICONS[number] || "🔒",
        anchor: `section-${number}-${slugify(sectionTitle)}`,
      };
      continue;
    }

    // Body
    if (current && line.trim() !== "") {
      current.content.push(line);
    }
  }

  if (current) sections.push(current);

  // fallback (if content doesn't have any numbered sections)
  if (sections.length === 0) {
    sections.push({
      number: 1,
      title,
      content: lines.filter((l) => l.trim() !== ""),
      icon: "🔒",
      anchor: `section-1-${slugify(title)}`,
    });
  }

  return { title, effectiveDate, sections };
}

export default function PrivacyPolicy() {
  const { language } = useLanguage();
  const [policyContent, setPolicyContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroContent, setHeroContent] = useState<{
    content_title: string;
    content_description?: string | null;
  } | null>(null);

  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setLoading(true);
        const response = await webInfoApi.getAll({ lang: language });
        const data = response.data?.data || response.data;

        if (data && data.length > 0) {
          const webInfo = data[0];
          setPolicyContent(
            webInfo.privacy_policy || "Privacy Policy content is not available at this time."
          );
        } else {
          setPolicyContent("Privacy Policy content is not available at this time.");
        }
      } catch (err) {
        console.log("Error fetching privacy policy:", err);
        setError("Failed to load Privacy Policy. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, [language]);

  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "privacy-policy", lang: language });
        const data = (response.data?.data || response.data) as Array<{
          content_title: string;
          content_description?: string | null;
        }>;

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching privacy hero content:", err);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  const parsed = useMemo(() => parsePolicy(policyContent), [policyContent]);

  // Basic markdown renderer:
  // - bullets starting with "- "
  // - bold text using **bold**
  const renderLines = (lines: string[]) => {
    const nodes: React.ReactNode[] = [];
    let bulletBuffer: string[] = [];

    const flushBullets = (keyPrefix: string) => {
      if (bulletBuffer.length === 0) return;
      nodes.push(
        <ul key={`${keyPrefix}-ul`} className="list-disc pl-6 mb-4">
          {bulletBuffer.map((b, i) => (
            <li
              key={`${keyPrefix}-li-${i}`}
              className="text-gray-700 mb-2 last:mb-0"
              dangerouslySetInnerHTML={{ __html: b }}
            />
          ))}
        </ul>
      );
      bulletBuffer = [];
    };

    lines.forEach((rawLine, idx) => {
      const line = rawLine.trim();

      // convert **bold** -> <strong>
      const htmlLine = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

      // bullets
      if (line.startsWith("- ")) {
        bulletBuffer.push(htmlLine.replace(/^- /, ""));
        return;
      }

      // if we were collecting bullets, flush first
      flushBullets(`b-${idx}`);

      // ignore empty
      if (!line) return;

      nodes.push(
        <p
          key={`p-${idx}`}
          className="text-gray-700 mb-4 last:mb-0 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: htmlLine }}
        />
      );
    });

    flushBullets("end");

    return <div>{nodes}</div>;
  };

  const TableOfContents = () => {
    const [tocRef, tocInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

    return (
      <div
        ref={tocRef}
        className={`bg-white rounded-lg border-2 border-[#0C2E6C] shadow-sm p-8 mb-8 -mt-30 reveal ${tocInView ? "is-visible" : ""
          }`}
      >
        <h2 className="text-2xl font-bold text-[#0C2E6C] mb-6">Table of Contents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {parsed.sections.map((section) => (
            <a
              key={section.anchor}
              href={`#${section.anchor}`}
              className="flex items-center gap-3 text-gray-700 hover:text-[#0C2E6C] transition-colors p-2 rounded hover:bg-[#E6F0FF]"
            >
              <span className="w-6 h-6 shrink-0" dangerouslySetInnerHTML={{ __html: section.icon }} />
              <span className="text-sm">
                {section.number}. {section.title}
              </span>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const SectionCard = ({ section }: { section: Section }) => {
    const [sectionRef, sectionInView] = useInView<HTMLDivElement>({ threshold: 0.1 });

    return (
      <div
        id={section.anchor}
        ref={sectionRef}
        className={`bg-white rounded-lg border border-gray-200 shadow-sm p-8 mb-6 reveal scroll-mt-20 ${sectionInView ? "is-visible" : ""
          }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-8 shrink-0" dangerouslySetInnerHTML={{ __html: section.icon }} />
          <h2 className="text-2xl font-bold text-black">
            {section.number}. {section.title}
          </h2>
        </div>

        {renderLines(section.content)}
      </div>
    );
  };

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        <Header />

        <HeroSection
          title={heroContent?.content_title || parsed.title || "Privacy Policy"}
          subtitle={heroContent?.content_description || "Your privacy is important to us. Learn how we collect, use, and protect your information."}
        />

        <section className="py-12 px-8 lg:px-16 bg-white">
          <div className="max-w-6xl mx-auto">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
              </div>
            )}

            {error && (
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">{error}</p>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* Effective Date / Last Updated */}
                <div
                  ref={contentRef}
                  className={`bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8 reveal ${contentInView ? "is-visible" : ""
                    }`}
                >
                  <p className="text-sm text-gray-500">
                    {parsed.effectiveDate
                      ? parsed.effectiveDate
                      : `Last Updated: ${new Date().toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}`}
                  </p>
                </div>

                {/* Table of Contents */}
                <TableOfContents />

                {/* Sections */}
                {parsed.sections.map((section) => (
                  <SectionCard key={section.anchor} section={section} />
                ))}

                {/* Contact */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 mt-8">
                  <p className="text-sm text-gray-600">
                    {language === "id"
                      ? "Jika Anda memiliki pertanyaan tentang Kebijakan Privasi ini, silakan hubungi kami di "
                      : "If you have any questions about this Privacy Policy, please contact us at "}
                    <a href="mailto:info@ganeshait.com" className="text-blue-600 underline">
                      info@ganeshait.com
                    </a>
                  </p>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
      <ChatWidget />
    </div>
  );
}
